
// Widens ng-select dropdown panel to fit the longest option, keeping it inside the viewport.
// ng-select itself only flips the panel vertically, it never adjusts the horizontal position.
import {afterNextRender, booleanAttribute, Directive, effect, inject, Injector, input, numberAttribute} from '@angular/core';
import {NgSelectComponent} from '@ng-select/ng-select';

const GUTTER = 8;

@Directive({
  selector: 'ng-select[ui-wide-dropdown]',
})
export class WideDropdownDirective {
  readonly enabled = input(true, {alias: 'ui-wide-dropdown', transform: booleanAttribute});
  readonly maxWidth = input(600, {alias: 'ui-wide-dropdown-max', transform: numberAttribute});

  private readonly select = inject(NgSelectComponent, {host: true});
  private readonly injector = inject(Injector);

  // Measures on open and on every later change of the option list: a select fed by a server
  // request gets its items after the panel is already open, so measuring only once on open
  // would size the panel against an empty list.
  private readonly fxMeasure = effect(() => {
    const isOpen = this.select.isOpen();
    this.select.items();
    if (isOpen) {
      this.measure();
    }
  });

  private measure() {
    if (!this.enabled()) {
      return;
    }
    afterNextRender(() => {
      const panel = document.getElementById(this.select.dropdownId);
      const origin = panel?.offsetParent ?? panel?.parentElement;
      if (!panel || !origin || document.documentElement.dir === 'rtl') {
        return;
      }

      const select = this.select.element.getBoundingClientRect();
      const viewport = document.documentElement.clientWidth;

      // Measure the natural width of the options
      panel.style.minWidth = '0';
      panel.style.maxWidth = 'none';
      panel.style.width = 'max-content';
      const list = panel.querySelector<HTMLElement>('.ng-dropdown-panel-items');
      const scrollbar = list ? list.offsetWidth - list.clientWidth : 0;
      const content = panel.offsetWidth + scrollbar;

      const width = Math.min(Math.max(content, select.width), this.maxWidth(), viewport - GUTTER * 2);
      // Grow to the right from the left edge of the input, or to the left from its right edge
      const left = select.left + width <= viewport - GUTTER
        ? select.left
        : Math.max(GUTTER, select.right - width);

      panel.style.width = `${width}px`;
      panel.style.minWidth = `${width}px`;
      panel.style.maxWidth = '';
      panel.style.left = `${left - origin.getBoundingClientRect().left}px`;
    }, {injector: this.injector});
  }
}
